import { Project, User } from '@clef/shared/types';
import MaterialTable, { Column } from '@material-table/core';
import { format } from 'date-fns';
import React, { useMemo, useState } from 'react';
import { useTypedSelector } from '@/hooks/useTypedSelector';
import { Box } from '@material-ui/core';
import { PROJECT_TYPE_ICON_LOOKUP } from '@/components/ProjectCard/NewProjectCard';
import useStyles from '../styles';
import { IconButton, Typography } from '@clef/client-library';
import { useDatasetMediaDetailsQuery } from '@/serverStore/dataset';
import ProjectFallbackImagePng from '@/images/project/project_fallback_image.png';
import { useGetProjectStatsByOrgIdQuery } from '@/serverStore/projects';
import HomeWorkIcon from '@material-ui/icons/HomeWorkOutlined';
import LockIcon from '@material-ui/icons/LockOutlined';
import MoreVert from '@material-ui/icons/MoreVert';
import MoreIconMenu, { MoreIconMenuItemKeys } from '@/components/ProjectCard/MoreIconMenu';
import { useSnackbar } from 'notistack';
import DeleteProjectDialog from '@/components/Dialogs/DeleteProjectDialog';
import AddUsersToProjectDialog from '@/components/AddUsersToProjectDialog/AddUsersToProjectDialog';
import EditProjectNameTextField from '@/components/Text/EditProjectNameTextField';

const ProjectCover: React.FC<{ project: Project }> = props => {
  const { project } = props;

  const styles = useStyles();

  const { data: mediaDetails, error: mediaDetailsError } = useDatasetMediaDetailsQuery({
    datasetId: project.datasetId,
    mediaId: project.coverMediaId,
  });
  if (mediaDetailsError || !mediaDetails?.url) {
    return <img src={ProjectFallbackImagePng} className={styles.projectListItemCoverImage} />;
  }
  return <img src={mediaDetails.url} className={styles.projectListItemCoverImage} />;
};

const ProjectListView: React.FC<{
  projects: Project[];
  onProjectItemClick: (projectId: number) => void;
}> = props => {
  const { projects, onProjectItemClick } = props;
  const { data: statsById } = useGetProjectStatsByOrgIdQuery();
  const currentUser = useTypedSelector(state => state.login.user!);
  const users = useTypedSelector(state => state.user.users);
  const [moreIconAnchorEl, setMoreIconAnchorEl] = useState<HTMLElement | null>(null);

  const { enqueueSnackbar } = useSnackbar();
  const [isEditingName, setIsEditingName] = useState(false);
  const [isInviteTeamMembersDialogOpen, setIsInviteTeamMembersDialogOpen] = useState(false);
  const [isDeleteProjectDialogOpen, setIsDeleteProjectDialogOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);

  const handleDeleteProjectDialogClose = () => setIsDeleteProjectDialogOpen(false);

  const handleMoreIconMenuClick = (key: MoreIconMenuItemKeys) => {
    switch (key) {
      case MoreIconMenuItemKeys.rename:
        setIsEditingName(true);
        break;
      case MoreIconMenuItemKeys.invite:
        setIsInviteTeamMembersDialogOpen(true);
        break;
      case MoreIconMenuItemKeys.delete:
        setIsDeleteProjectDialogOpen(true);
        break;
    }
  };

  const userById = useMemo(() => {
    return users.reduce((acc, user) => {
      acc[user.id] = user;
      return acc;
    }, {} as Record<string, User>);
  }, [users]);

  const styles = useStyles();
  const columns: Column<Project>[] = [
    {
      title: t('Name'),
      field: 'name',
      width: 300,
      render: project => {
        return (
          <Box display="flex" alignItems="center">
            <Box mr={4}>
              <ProjectCover project={project} />
            </Box>
            <Box className={styles.projectIconContainer}>
              {project.labelType && PROJECT_TYPE_ICON_LOOKUP[project.labelType]}
            </Box>
            {isEditingName && selectedProject?.id === project.id ? (
              <EditProjectNameTextField
                name={project.name}
                projectId={project.id}
                setIsEditingName={setIsEditingName}
              />
            ) : (
              <Typography variant="body_semibold">{project.name}</Typography>
            )}
          </Box>
        );
      },
    },
    {
      title: t('Creator'),
      width: 150,
      render: project => {
        const ownerId = project.usersRoles?.find(user => user.role === 'owner')?.userId ?? '';
        const owner = userById[ownerId];
        if (!owner) {
          return '-';
        }
        const ownerName = [owner.name, owner.lastName].filter(Boolean).join(' ');
        const displayName =
          owner.id === currentUser.id ? t('{{ownerName}} (You)', { ownerName }) : ownerName;
        return (
          <Box display="flex" alignItems="center">
            <Box mr={2}>
              {project.inviteOnly ? (
                <LockIcon fontSize="small" />
              ) : (
                <HomeWorkIcon fontSize="small" />
              )}
            </Box>
            <Typography variant="body_semibold">{displayName}</Typography>
          </Box>
        );
      },
      customSort: (a, b) => {
        const ownerIdA = a.usersRoles?.find(user => user.role === 'owner')?.userId ?? '';
        const ownerIdB = b.usersRoles?.find(user => user.role === 'owner')?.userId ?? '';
        const ownerA = userById[ownerIdA];
        const ownerB = userById[ownerIdB];
        const ownerNameA = [ownerA?.name, ownerA?.lastName].filter(Boolean).join(' ');
        const ownerNameB = [ownerB?.name, ownerB?.lastName].filter(Boolean).join(' ');
        if (!ownerNameA || !ownerNameB) {
          const emailA = ownerA?.email || '';
          const emailB = ownerB?.email || '';
          return emailA.localeCompare(emailB);
        }
        return ownerNameA.localeCompare(ownerNameB);
      },
    },
    {
      title: t('Images'),
      width: 80,
      render: project => {
        return statsById?.mediaCountByDatasetId[project.datasetId!] || 0;
      },
      customSort: (a, b) => {
        const countA = statsById?.mediaCountByDatasetId[a.datasetId!] || 0;
        const countB = statsById?.mediaCountByDatasetId[b.datasetId!] || 0;
        return countA - countB;
      },
    },
    {
      title: t('Classes'),
      width: 80,
      render: project => {
        return statsById?.classCountByProjectId[project.id!] || 0;
      },
      customSort: (a, b) => {
        const countA = statsById?.classCountByProjectId[a.id!] || 0;
        const countB = statsById?.classCountByProjectId[b.id!] || 0;
        return countA - countB;
      },
    },
    {
      title: t('Models'),
      width: 80,
      render: project => {
        return statsById?.modelCountByProjectId[project.id!] || 0;
      },
      customSort: (a, b) => {
        const countA = statsById?.modelCountByProjectId[a.id!] || 0;
        const countB = statsById?.modelCountByProjectId[b.id!] || 0;
        return countA - countB;
      },
    },
    {
      title: t('Created'),
      width: 150,
      render: project => {
        return format(new Date(project.createdAt), 'MMM dd, yyyy');
      },
      customSort: (a, b) => {
        return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
      },
      defaultSort: 'desc',
    },
    {
      title: t('Last opened'),
      width: 150,
      render: project => {
        const owner = project.usersRoles?.find(user => user.role === 'owner');
        if (!owner?.lastOpenedTime) {
          return '-';
        }
        return format(new Date(owner.lastOpenedTime), 'MMM dd, yyyy');
      },
      customSort: (a, b) => {
        const ownerA = a.usersRoles?.find(user => user.role === 'owner');
        const ownerB = b.usersRoles?.find(user => user.role === 'owner');
        const lastOpenedA = new Date(ownerA?.lastOpenedTime || 0).getTime();
        const lastOpenedB = new Date(ownerB?.lastOpenedTime || 0).getTime();
        return lastOpenedA - lastOpenedB;
      },
      defaultSort: 'desc',
    },
    {
      title: t('Actions'),
      width: 60,
      render: project => {
        const owner = project.usersRoles?.find(user => user.role === 'owner');
        const isCurrentUserOwner = owner?.userId === currentUser.id;
        if (!isCurrentUserOwner) {
          return null;
        }
        return (
          <Box display="flex" alignItems="center">
            <IconButton
              data-testid="fre_projects_actions_button"
              aria-controls="fre_projects_actions_menu"
              aria-haspopup="true"
              onClick={e => {
                e.stopPropagation();
                setSelectedProject(project);
                setMoreIconAnchorEl(e.currentTarget);
              }}
            >
              <MoreVert />
            </IconButton>
          </Box>
        );
      },
      sorting: false,
    },
  ];
  return (
    <>
      <MaterialTable
        columns={columns}
        data={projects}
        options={{
          search: false,
          showTitle: false,
          pageSize: 10,
          pageSizeOptions: [10, 25, 50, 100],
          toolbar: false,
        }}
        onRowClick={(event, rowData) => rowData && onProjectItemClick(rowData.id)}
      />
      {selectedProject && (
        <>
          <MoreIconMenu
            anchorEl={moreIconAnchorEl}
            hideInvite={!selectedProject.inviteOnly}
            onClose={() => setMoreIconAnchorEl(null)}
            onBlur={() => setMoreIconAnchorEl(null)}
            onItemClick={(e, key) => {
              e.stopPropagation();
              setMoreIconAnchorEl(null);
              const owner = selectedProject.usersRoles?.find(user => user.role === 'owner');
              const isCurrentUserOwner = owner?.userId === currentUser.id;
              if (!isCurrentUserOwner) {
                enqueueSnackbar(t('Only project owner can perform this operation'), {
                  variant: 'info',
                });
                return;
              }
              handleMoreIconMenuClick(key);
            }}
          />
          <DeleteProjectDialog
            project={selectedProject}
            open={isDeleteProjectDialogOpen}
            handleClose={handleDeleteProjectDialogClose}
          />
          <AddUsersToProjectDialog
            projectId={selectedProject.id}
            open={isInviteTeamMembersDialogOpen}
            onCancel={() => setIsInviteTeamMembersDialogOpen(false)}
          />
        </>
      )}
    </>
  );
};

export default ProjectListView;
